/* import __COLOCATED_TEMPLATE__ from './standalone-add-knowledge-modal.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { EntityType } from 'embercom/models/data/entity-types';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import { type FinAIType } from 'embercom/lib/knowledge-hub/constants';
import { type NavigableViewId, type ViewId } from 'embercom/components/knowledge-hub/setup-modal';
import { action } from '@ember/object';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type Store from '@ember-data/store';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { objectIcons } from 'embercom/models/data/matching-system/matching-constants';
import type RouterService from '@ember/routing/router-service';

interface Args {
  modal: any;
  title: string;
  onNavigationBack: () => void;
  onNavigationNext: (viewId: ViewId) => void;
  showBackButton: boolean;
  onModalClose: () => void;
  context?: {
    folder?: string;
    entityTypes?: EntityType[];
    aiTypeFilter?: FinAIType;
    includeNonKnowledgeBaseContent?: boolean;
    openInNewTab?: boolean;
  };
}

const CONTENT_ENTITY_TYPES = [
  EntityType.ContentSnippet,
  EntityType.FileSourceContent,
  EntityType.InternalArticle,
];

export default class StandaloneAddKnowledgeModal extends Component<Args> {
  @service declare appService: AppService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare modalService: $TSFixMe;

  get app() {
    return this.appService.app;
  }

  get contentEntityTypes() {
    return CONTENT_ENTITY_TYPES;
  }

  get contentTiles() {
    let localizationPrefix = 'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles';
    return this.contentEntityTypes
      .filter((entityType) => {
        return !this.isCopilotOnly(entityType) || this.appService.app.canUseCopilotOnStandalone;
      })
      .map((entityType) => {
        return {
          id: `${entityType}`,
          title: this.intl.t(`${localizationPrefix}.${objectNames[entityType]}.title`),
          entityType,
          description: this.intl.t(
            `${localizationPrefix}.${objectNames[entityType]}.description-ai-agent`,
          ),
          icon: objectIcons[entityType],
          copilotOnly: this.isCopilotOnly(entityType),
        };
      });
  }

  get importTiles() {
    let tiles = [];

    if (this.app.hasStandalonePlatform('zendesk')) {
      tiles.push(
        this.createTile({
          id: 'zendesk',
          viewId: 'zendesk-sync',
          titleKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.zendesk.standalone-title',
          descriptionKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.zendesk.description-ai-agent',
          icon: 'zendesk' as InterfaceIconName,
          entityType: EntityType.ArticleContent,
        }),
      );
    }

    if (this.app.hasStandalonePlatform('salesforce')) {
      tiles.push(
        this.createTile({
          id: 'salesforce_knowledge',
          viewId: 'salesforce-sync',
          titleKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.salesforce.title',
          descriptionKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.salesforce.description-ai-agent',
          icon: 'salesforce' as InterfaceIconName,
          entityType: EntityType.ExternalContent,
        }),
      );
    }

    if (this.app.hasStandalonePlatform('freshdesk')) {
      tiles.push(
        this.createTile({
          id: 'freshdesk',
          viewId: 'freshdesk-sync',
          titleKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.freshdesk.title',
          descriptionKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.freshdesk.description-ai-agent',
          icon: 'freshdesk' as InterfaceIconName,
          entityType: EntityType.ExternalContent,
        }),
      );
    }

    tiles.push(
      this.createTile({
        id: 'external-content',
        viewId: 'external-content',
        titleKey:
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.external-content.standalone-title',
        descriptionKey:
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.external-content.description-ai-agent',
        entityType: EntityType.ExternalContent,
      }),
    );

    tiles.push(
      this.createTile({
        id: 'notion',
        viewId: 'notion-sync',
        titleKey:
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.standalone-notion.title',
        descriptionKey:
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.standalone-notion.description',
        icon: 'notion' as InterfaceIconName,
        entityType: EntityType.InternalArticle,
      }),
      this.createTile({
        id: 'confluence',
        viewId: 'confluence-sync',
        titleKey:
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.standalone-confluence.title',
        descriptionKey:
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.standalone-confluence.description',
        icon: 'confluence' as InterfaceIconName,
        entityType: EntityType.InternalArticle,
      }),
      this.createTile({
        id: 'guru',
        viewId: 'guru-sync',
        titleKey:
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.standalone-guru.title',
        descriptionKey:
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.standalone-guru.description',
        icon: 'guru' as InterfaceIconName,
        entityType: EntityType.InternalArticle,
      }),
    );

    if (this.appService.app.canUseDocument360Standalone) {
      tiles.push(
        this.createTile({
          id: 'document360',
          viewId: 'document360-sync',
          titleKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.document360.title',
          descriptionKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.document360.description',
          icon: 'document360' as InterfaceIconName,
          entityType: EntityType.FileSourceContent,
        }),
      );
    }

    if (this.appService.app.canUseBoxStandalone) {
      tiles.push(
        this.createTile({
          id: 'box',
          viewId: 'box-sync',
          titleKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.standalone-box.title',
          descriptionKey:
            'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.standalone-box.description',
          icon: 'boxinc' as InterfaceIconName,
          entityType: EntityType.FileSourceContent,
        }),
      );
    }

    return tiles.filter((tile) => {
      switch (tile.id) {
        case 'zendesk':
        case 'salesforce_knowledge':
          return this.appService.app.isStandaloneApp;
        default:
          return true;
      }
    });
  }

  private createTile({
    id,
    viewId,
    titleKey,
    descriptionKey,
    icon,
    entityType,
  }: {
    id: string;
    viewId: string;
    titleKey: string;
    descriptionKey?: string;
    icon?: InterfaceIconName;
    entityType: EntityType;
  }) {
    return {
      id,
      viewId,
      title: this.intl.t(titleKey),
      description: descriptionKey ? this.intl.t(descriptionKey) : undefined,
      icon,
      entityType,
      copilotOnly: this.isCopilotOnly(entityType),
    };
  }

  private isCopilotOnly(entityType: EntityType) {
    let usageSummaryForEntity = this.knowledgeHubService.usageSummary?.[entityType];

    if (usageSummaryForEntity) {
      return (
        usageSummaryForEntity.is_eligible_for_copilot &&
        !usageSummaryForEntity.is_eligible_for_chatbot
      );
    }

    return false;
  }

  @action async navigateToView(viewId: NavigableViewId, entityType?: EntityType) {
    try {
      await this.knowledgeHubService.ensurePermissions(entityType);
    } catch (error) {
      this.args.onModalClose();
      return;
    }

    if (viewId === 'salesforce-sync') {
      this.args.onModalClose();

      this.router.transitionTo(this.router.currentRouteName, {
        queryParams: {
          openSalesforceSync: true,
        },
      });
      return;
    }

    if (viewId === 'confluence-sync') {
      this.args.onModalClose();
      this.modalService.openModal(
        'react/knowledge-hub/add-new-external-source/add-new-external-source-sheet',
        {
          open: true,
          appId: this.appService.app.id,
          adminId: this.appService.app.currentAdmin.id.toString(),
          initialSourceId: 'confluence',
          onClose: () => this.modalService.closeModal(),
        },
      );
      return;
    }

    this.args.onNavigationNext(viewId);
  }

  @action async createContent(entityType: EntityType) {
    if (entityType === EntityType.FileSourceContent) {
      await this.navigateToView('file-upload', entityType);
      return;
    }

    this.knowledgeHubService.createContent(
      entityType,
      { folder_id: this.args.context?.folder },
      this.args.context?.openInNewTab,
    );
    this.args.onModalClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Channels::StandaloneAddKnowledgeModal': typeof StandaloneAddKnowledgeModal;
    'standalone/channels/standalone-add-knowledge-modal': typeof StandaloneAddKnowledgeModal;
  }
}
